import { useMemo } from 'react'
import '../../styles/data-table.css'

/**
 * Componente de tabla para la visualización y gestión de los estados de tickets.
 * * **Características principales**:
 * - Ordenamiento automático por `orderIndex`.
 * - Manejo de estados de carga (spinner) y lista vacía.
 * - Tabla compacta compartida (`data-table.css`) con tokens --gm-* (claro/oscuro).
 * - Color del estado como punto junto al nombre y distintivo de tipo (Abierto/Cierre).
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Array<Object>} [props.data=[]] - Colección de objetos de estado.
 * @param {number|string} props.data[].id - Identificador único.
 * @param {string} props.data[].name - Nombre visible del estado.
 * @param {string} props.data[].color - Código de color hexadecimal (ej: #FF0000).
 * @param {number} props.data[].orderIndex - Valor numérico para definir la prioridad de orden.
 * @param {boolean} props.data[].isClosed - Define si el estado representa el fin del flujo del ticket.
 * @param {boolean} props.loading - Indica si la aplicación está consultando datos.
 * @param {Function} [props.onEdit] - Callback ejecutado al presionar editar. Recibe el objeto del estado seleccionado.
 * * @returns {JSX.Element} Una tabla responsiva o indicadores de estado (Loading/Empty).
 */
const StatusTable = ({ data = [], loading, onEdit }) => {
  /**
   * Memoriza la lista ordenada para evitar cálculos innecesarios en cada renderizado,
   * a menos que la propiedad `data` cambie.
   */
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => a.orderIndex - b.orderIndex)
  }, [data])

  /** Mensaje a mostrar en lugar de filas (carga o lista vacía). */
  const placeholder = loading ? (
    <span className="spinner-border spinner-border-sm text-primary" />
  ) : !sortedData.length ? (
    <>
      <i className="fa-solid fa-layer-group d-block fs-4 mb-2 opacity-50" />
      No hay estados configurados. Agrega uno con «Nuevo estado».
    </>
  ) : null

  return (
    <div className="tt st-table" style={{ flex: 'none' }}>
      <div className="tt-scroll">
        <table className="tt-table">
          <thead>
            <tr>
              <th>Estado</th>
              <th style={{ width: 90 }}>Orden</th>
              <th style={{ width: 130 }}>Tipo</th>
              <th style={{ width: 80 }} />
            </tr>
          </thead>

          <tbody>
            {placeholder ? (
              <tr>
                <td colSpan={4} className="tt-empty">
                  {placeholder}
                </td>
              </tr>
            ) : (
              sortedData.map((status) => (
                <tr key={status.id}>
                  <td>
                    <span className="d-inline-flex align-items-center gap-2">
                      <span
                        className="tt-dot"
                        style={{ background: status.color }}
                        title={status.color?.toUpperCase()}
                      />
                      <span className="fw-semibold">{status.name}</span>
                    </span>
                  </td>

                  <td className="tt-muted">{status.orderIndex}</td>

                  <td>
                    <span
                      className={`tt-badge${status.isClosed ? '' : ' gm-tono'}`}
                      style={
                        status.isClosed
                          ? {
                              color: 'var(--gm-text-muted)',
                              background: 'var(--gm-surface-2)',
                            }
                          : { '--tono': status.color }
                      }
                    >
                      <i
                        className={`fa-solid ${
                          status.isClosed ? 'fa-lock' : 'fa-circle-play'
                        }`}
                      />
                      {status.isClosed ? 'Cierre' : 'En proceso'}
                    </span>
                  </td>

                  <td>
                    <div className="tt-actions">
                      <button
                        type="button"
                        className="tt-icon-btn"
                        title="Editar"
                        onClick={() => onEdit && onEdit(status)}
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        type="button"
                        className="tt-icon-btn is-danger"
                        title="Eliminar"
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        .st-table tbody tr:hover { background: var(--gm-surface-2); }
        .st-table tbody tr:hover .tt-actions { opacity: 1; }
        .st-table tbody tr:last-child td { border-bottom: none; }
      `}</style>
    </div>
  )
}

export default StatusTable
